import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { styled, useTheme } from '@mui/material/styles';
import { Box, Drawer, useMediaQuery } from '@mui/material';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import useLocales from '../../hooks/useLocales';
import buildNavConfig from './SidebarConfig';
import CreatePopover from './CreatePopover';
import useSidebarResize, { DEFAULT_SIDEBAR_WIDTH } from './useSidebarResize';

type NavConfig = {
  subheader: string;
  items: {
    title: string;
    path?: string;
    icon?: React.JSX.Element;
    exact?: boolean;
    info?: React.JSX.Element;
  }[];
}[];

const DRAWER_WIDTH = DEFAULT_SIDEBAR_WIDTH;
const DRAWER_WIDTH_COLLAPSED = 88;
const HEADER_HEIGHT = 64;
const RootStyle = styled('div', {
  shouldForwardProp: (prop) => !['collapsed', 'expandedWidth', 'resizing'].includes(String(prop))
})<{ collapsed?: boolean; expandedWidth: number; resizing: boolean }>(
  ({ theme, collapsed, expandedWidth, resizing }) => ({
    [theme.breakpoints.up('lg')]: {
      flexShrink: 0,
      width: collapsed ? DRAWER_WIDTH_COLLAPSED : expandedWidth,
      transition: resizing
        ? 'none'
        : theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
          })
    }
  })
);

type DashboardSidebarProps = {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
  onToggleCollapse?: VoidFunction;
  isCollapsed: boolean;
  navConfig?: NavConfig;
  footer?: React.ReactNode;
};

export default function DashboardSidebar({
  isOpenSidebar,
  onCloseSidebar,
  onToggleCollapse,
  isCollapsed,
  navConfig: navConfigProp,
  footer
}: DashboardSidebarProps) {
  const theme = useTheme();
  const { translate } = useLocales();
  const { expandedWidth, resizing, minWidth, maxWidth, handleProps } = useSidebarResize();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { pathname } = useLocation();

  const enrichedConfig = useMemo(() => navConfigProp ?? buildNavConfig([]), [navConfigProp]);

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (collapsed = false) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Scrollbar
        sx={{
          flexGrow: 1,
          '& .simplebar-content': { display: 'flex', flexDirection: 'column' }
        }}
      >
        <NavSection
          navConfig={enrichedConfig}
          collapsed={collapsed}
          onToggleCollapse={onToggleCollapse}
          leadingAction={<CreatePopover collapsed={collapsed} />}
        />
      </Scrollbar>
      {footer && (
        <Box
          sx={{
            px: 2,
            pb: 2,
            pt: 1,
            flexShrink: 0,
            ...(collapsed && { display: 'flex', justifyContent: 'center' })
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  );

  return (
    <RootStyle
      collapsed={isDesktop ? isCollapsed : false}
      expandedWidth={expandedWidth}
      resizing={resizing}
    >
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          sx={{
            pointerEvents: 'none',
            '& .MuiBackdrop-root': { top: HEADER_HEIGHT, pointerEvents: 'auto' }
          }}
          slotProps={{
            paper: {
              sx: {
                width: DRAWER_WIDTH,
                bgcolor: 'background.default',
                top: HEADER_HEIGHT,
                height: `calc(100% - ${HEADER_HEIGHT}px)`,
                pointerEvents: 'auto'
              }
            }
          }}
        >
          {renderContent(false)}
        </Drawer>
      )}
      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          slotProps={{
            paper: {
              sx: {
                width: isCollapsed ? DRAWER_WIDTH_COLLAPSED : expandedWidth,
                top: HEADER_HEIGHT,
                height: `calc(100% - ${HEADER_HEIGHT}px)`,
                bgcolor: 'background.default',
                overflowX: 'hidden',
                transition: resizing
                  ? 'none'
                  : theme.transitions.create('width', {
                      easing: theme.transitions.easing.sharp,
                      duration: theme.transitions.duration.enteringScreen
                    })
              }
            }
          }}
        >
          {renderContent(isCollapsed)}
          {!isCollapsed && (
            <Box
              role="separator"
              aria-label={translate('menu.resize_sidebar')}
              aria-orientation="vertical"
              aria-valuemin={minWidth}
              aria-valuemax={maxWidth}
              aria-valuenow={expandedWidth}
              tabIndex={0}
              {...handleProps}
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                right: 0,
                width: 6,
                cursor: 'col-resize',
                touchAction: 'none',
                zIndex: 1,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  right: 0,
                  width: 2,
                  bgcolor: resizing ? 'action.selected' : 'transparent'
                },
                '&:hover::after': { bgcolor: 'action.selected' },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: -2
                }
              }}
            />
          )}
        </Drawer>
      )}
    </RootStyle>
  );
}
